import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Slider,
  Typography,
} from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLocale } from '@refinedev/core';
import {
  HYPOTHESIS_HELD_VALUES,
  emotionCatalogue,
  feelingCatalogue,
  observationInputFromForm,
  type ObservationFields,
  type ObservationFormValues,
} from './esHelpers';
import useStyles from './styles.style';

const { Text } = Typography;
const { OptGroup, Option } = Select;

/**
 * E02 · Record / Edit Observation (portal, p4_observe) — the drawer the Experiment Detail view opens
 * for "+ Add observation" (RecordObservation) and for each recorded run (UpdateObservation, the
 * v2 editable observations). The REAL L02 observation field set (D-v2-observation-fields):
 * whatHappened · behaviors · emotions (ids + strengths) · body sensations · which hypothesis held
 * (A / B / mixed) · belief rating 0–100 · learnings. Emotion / sensation ids are the therapist's
 * pick from the shared catalogues, exactly as the shipped form picks them.
 */
export const ObservationForm = ({
  open,
  title,
  subtitle,
  initial,
  saving,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  /** Edit-loaded values for an existing run; undefined = a new run. */
  initial?: ObservationFormValues;
  saving: boolean;
  onClose: () => void;
  onSubmit: (fields: ObservationFields) => Promise<void> | void;
}) => {
  const { t } = useTranslation();
  const getLocale = useGetLocale();
  const locale = getLocale();
  const { styles } = useStyles();
  const [form] = Form.useForm<ObservationFormValues>();

  const emotions = useMemo(() => emotionCatalogue(locale), [locale]);
  const feelings = useMemo(() => feelingCatalogue(locale), [locale]);
  const flatEmotions = useMemo(
    () => emotions.flatMap((g) => g.children),
    [emotions],
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(
        initial ?? {
          whatHappened: '',
          behaviors: '',
          emotionIds: [],
          emotionStrengths: {},
          feelings: [],
          whichHypothesisHeld: null,
          rating: null,
          learnings: '',
        },
      );
    }
  }, [open, initial, form]);

  const selectedEmotionIds = Form.useWatch('emotionIds', form) ?? [];
  const hypothesisHeldOptions = t(
    'pages.behaviorExperiment.hypothesisHeldOptions',
    {
      returnObjects: true,
    },
  ) as unknown as string[];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      title={title}
      destroyOnClose
      data-testid="cc-behavior-experiment-observation-drawer"
      extra={
        <Button
          type="primary"
          loading={saving}
          onClick={() => form.submit()}
          data-testid="cc-behavior-experiment-observation-save"
        >
          {t('pages.behaviorExperiment.saveObservation')}
        </Button>
      }
    >
      {subtitle ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {subtitle}
        </Text>
      ) : null}
      <Form<ObservationFormValues>
        form={form}
        layout="vertical"
        onFinish={(values) => onSubmit(observationInputFromForm(values))}
        data-testid="cc-behavior-experiment-observation-form"
      >
        <Form.Item
          name="whatHappened"
          label={t('pages.behaviorExperiment.whatHappened')}
        >
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder={t('pages.behaviorExperiment.whatHappenedPlaceholder')}
            data-testid="cc-behavior-experiment-observation-what-happened"
          />
        </Form.Item>

        <Form.Item
          name="behaviors"
          label={t('shared.behaviorExperiment.actualBehavior')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t(
              'shared.behaviorExperiment.actualBehaviorPlaceholder',
            )}
            data-testid="cc-behavior-experiment-observation-behaviors"
          />
        </Form.Item>

        <Form.Item
          name="emotionIds"
          label={t('shared.behaviorExperiment.actualEmotions')}
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="children"
            data-testid="cc-behavior-experiment-observation-emotions"
          >
            {emotions.map((group) => (
              <OptGroup label={group.name} key={group.name}>
                {group.children.map((e) => (
                  <Option key={e.id} value={e.id}>
                    {e.name}
                  </Option>
                ))}
              </OptGroup>
            ))}
          </Select>
        </Form.Item>

        {(selectedEmotionIds as number[]).map((id) => (
          <div key={id} className={styles['behavior-experiments--slider-row']}>
            <Form.Item
              name={['emotionStrengths', String(id)]}
              label={flatEmotions.find((e) => e.id === id)?.name ?? String(id)}
              initialValue={50}
              style={{ flex: 1, minWidth: 260 }}
            >
              <Slider
                min={1}
                max={100}
                data-testid={`cc-behavior-experiment-observation-emotion-strength-${id}`}
              />
            </Form.Item>
            <Form.Item name={['emotionStrengths', String(id)]}>
              <InputNumber min={1} max={100} />
            </Form.Item>
          </div>
        ))}

        <Form.Item
          name="feelings"
          label={t('shared.behaviorExperiment.actualFeelings')}
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="children"
            data-testid="cc-behavior-experiment-observation-feelings"
          >
            {feelings.map((group) => (
              <OptGroup label={group.name} key={group.name}>
                {group.children.map((f) => (
                  <Option key={f.id} value={f.id}>
                    {f.name}
                  </Option>
                ))}
              </OptGroup>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="whichHypothesisHeld"
          label={t('pages.behaviorExperiment.whichHypothesisHeld')}
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            data-testid="cc-behavior-experiment-observation-hypothesis-held"
          >
            {HYPOTHESIS_HELD_VALUES.map((value) => (
              // the testid rides the LABEL, not the Radio.Button: antd spreads rest props onto the
              // visually-hidden <input>, which nothing can click
              <Radio.Button key={value} value={value}>
                <span
                  data-testid={`cc-behavior-experiment-observation-hypothesis-held-${value}`}
                >
                  {hypothesisHeldOptions?.[value] ?? String(value)}
                </span>
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        <div className={styles['behavior-experiments--slider-row']}>
          <Form.Item
            name="rating"
            label={t('pages.behaviorExperiment.rating')}
            style={{ flex: 1, minWidth: 260 }}
          >
            <Slider
              min={0}
              max={100}
              data-testid="cc-behavior-experiment-observation-rating"
            />
          </Form.Item>
          <Form.Item name="rating">
            <InputNumber
              min={0}
              max={100}
              data-testid="cc-behavior-experiment-observation-rating-input"
            />
          </Form.Item>
        </div>

        <Form.Item
          name="learnings"
          label={t('pages.behaviorExperiment.learnings')}
        >
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder={t('pages.behaviorExperiment.learningsPlaceholder')}
            data-testid="cc-behavior-experiment-observation-learnings"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
